import type { Configuration, PopupRequest } from '@azure/msal-browser';

/**
 * MSAL configuration for the Whānau Factor Profile SPA.
 *
 * PHASE 0 PLACEHOLDER — the WFP app registration does not exist yet.
 *
 * Once Sam provisions `WFP SPA` in Entra ID:
 *   1. Replace `WFP_SPA_CLIENT_ID_PLACEHOLDER` with the real client ID
 *   2. Replace `WFP_API_SCOPE_PLACEHOLDER` with the real API scope (e.g. `api://<wfp-api-app-id>/access_as_user`)
 *   3. Confirm redirect URIs match the deployment URL (`https://whanaufactor.nash.org.nz`)
 *
 * Tenant ID is taken from the verified NASH tenant captured during Phase 0 discovery.
 *
 * The config intentionally fails loudly on dev startup if placeholders are still present —
 * better to break the dev server than silently auth against the wrong identity.
 *
 * See docs/ADR-001-platform-architecture.md §2 "Identity" for the full posture.
 */

const NASH_TENANT_ID = 'c9bcba1d-ce80-4690-86ff-2419660e545f';

// WFP SPA app registration created 2026-05-09. Single-tenant, SPA platform,
// redirect URIs http://localhost:5173 and https://whanaufactor.nash.org.nz.
const CLIENT_ID = '8bf08419-6491-4050-aaa7-c8d8826abd01';

// WFP API app registration created 2026-05-09. Exposed scope `access_as_user`
// is pre-authorised for the SPA app reg above.
const API_CLIENT_ID = '027c480c-382f-4629-8a6d-edeea57bd6a6';
const API_SCOPE = `api://${API_CLIENT_ID}/access_as_user`;

export const msalConfig: Configuration = {
  auth: {
    clientId: CLIENT_ID,
    authority: `https://login.microsoftonline.com/${NASH_TENANT_ID}`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
    navigateToLoginRequestUrl: true,
  },
  cache: {
    // sessionStorage is the safer default for whānau data — tokens don't survive a tab close.
    // localStorage gives smoother UX across tabs but persists tokens on shared tablets.
    // Phase 1 to revisit when the tablet fleet decision (task #10) lands.
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) return;
        // Wire to App Insights in Phase 1.
        if (level <= 1) console.error('[MSAL]', message);
      },
      piiLoggingEnabled: false,
      logLevel: 1, // Error only by default; raise in dev as needed.
    },
  },
};

/**
 * Scopes for the initial sign-in popup. Add API-specific scopes here when the WFP API
 * app registration exists.
 */
export const loginRequest: PopupRequest = {
  scopes: ['openid', 'profile', 'User.Read'],
};

/**
 * Scopes used when silently acquiring a token to call the WFP API.
 */
export const apiTokenRequest: PopupRequest = {
  scopes: [API_SCOPE],
};
